import type { ReactNode } from 'react'
import { ImageIcon, X } from 'lucide-react'
import { formatFechaPublicacion } from '@/features/noticias/format'
import { cn } from '@/lib/utils'

interface MobilePreviewProps {
  titulo: string
  descripcion: string
  ctaTexto: string
  fechaPublicacion: string
  featured: boolean
  imageSrc: string | null
}

// Muestra dos variantes encuadradas en un "viewport" mobile (~360x720).
// - Si featured: la toma fullscreen encima.
// - La card del feed siempre se muestra como referencia secundaria.
export function MobilePreview(props: MobilePreviewProps): ReactNode {
  return (
    <div className="flex flex-col items-center gap-6">
      <PreviewFrame label={props.featured ? 'Fullscreen (destacada)' : 'Fullscreen (se muestra solo si featured)'}>
        <FullscreenPreview {...props} />
      </PreviewFrame>

      <PreviewFrame label="Card en el feed">
        <FeedCardPreview {...props} />
      </PreviewFrame>
    </div>
  )
}

function PreviewFrame({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactNode {
  return (
    <div className="flex w-full flex-col items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="relative h-[640px] w-[320px] overflow-hidden rounded-[2.25rem] border-[10px] border-neutral-900 bg-neutral-950 shadow-xl">
        <div className="absolute inset-0 overflow-hidden">{children}</div>
      </div>
    </div>
  )
}

function FullscreenPreview({
  titulo,
  descripcion,
  ctaTexto,
  imageSrc,
  featured,
}: MobilePreviewProps): ReactNode {
  return (
    <div
      className={cn(
        'flex h-full w-full flex-col bg-neutral-900 text-white',
        !featured && 'opacity-50',
      )}
    >
      <div className="relative flex-1">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-800 text-neutral-500">
            <ImageIcon className="h-10 w-10" />
          </div>
        )}
        <button
          type="button"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white"
          aria-label="Cerrar"
          tabIndex={-1}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-col gap-3 bg-neutral-950 p-4">
        <h2 className="text-lg font-semibold leading-tight">
          {titulo || 'Título de la noticia'}
        </h2>
        <p className="line-clamp-4 text-sm text-neutral-300">
          {descripcion || 'Descripción de la noticia...'}
        </p>
        <button
          type="button"
          className="mt-1 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
          tabIndex={-1}
        >
          {ctaTexto || 'Más info'}
        </button>
        <button
          type="button"
          className="rounded-md border border-neutral-700 px-3 py-2 text-xs text-neutral-300"
          tabIndex={-1}
        >
          Cerrar
        </button>
      </div>
    </div>
  )
}

function FeedCardPreview({
  titulo,
  descripcion,
  ctaTexto,
  fechaPublicacion,
  imageSrc,
}: MobilePreviewProps): ReactNode {
  return (
    <div className="flex h-full w-full flex-col bg-neutral-100 p-4">
      <span className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Novedades
      </span>
      <div className="overflow-hidden rounded-2xl bg-white shadow">
        <div className="relative aspect-[16/10] w-full bg-neutral-200">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-neutral-400">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 p-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
            {formatFechaPublicacion(fechaPublicacion)}
          </span>
          <h3 className="text-sm font-semibold text-neutral-900">
            {titulo || 'Título de la noticia'}
          </h3>
          <p className="line-clamp-2 text-xs text-neutral-600">
            {descripcion || 'Descripción de la noticia...'}
          </p>
          <button
            type="button"
            className="mt-1 self-start rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
            tabIndex={-1}
          >
            {ctaTexto || 'Más info'}
          </button>
        </div>
      </div>
    </div>
  )
}
